import { useEffect, useState } from 'react';
import cx from 'classnames';
import { isDesktop } from 'react-device-detect';
import { useDispatch } from 'react-redux';

import {
  AppTracker,
  BottomPanelKey,
  ChannelSchema,
  EventLeaveReason,
  EventSchema,
  EventStatus,
  UserActionType
} from 'types';
import { displayCount, getChannelURL, getEventURL, UZBEKITTV_CHANNEL_NAME } from 'utils';
import { profileActions } from 'store/actions';
import {
  authSelector,
  channelByNameSelector,
  hasAvailableMinutesSelector,
  hasChannelSelector,
  isMemberSelector,
  isSubscribedSelector
} from 'store/selectors';
import { callbackTriggersActions } from 'store/slices/callbackTriggers';
import { api, LoggerService, tracker } from 'services';
import { useBottomSheet, useRouter, useStore, useToast } from 'hooks';
import { BottomPanels, EditChannelPanel, Navbar } from 'containers';
import { Card, PostEventCard } from 'components';
import { CheckedIcon, EditIcon, NoPhotoIcon, ShareIcon, Spinner } from 'components/Icon';

import ChannelEvents from './ChannelEvents';
import SharePanel from './SharePanel';

import styles from './ChannelDetails.module.scss';

export type ChannelDetailsProps = {
  channelName: string;
  onBecomeMember?(channelId: string, channelName: string): void;
};

const ChannelDetails = ({ channelName, onBecomeMember }: ChannelDetailsProps): JSX.Element => {
  const dispatch = useDispatch();
  const router = useRouter<{ eventCode?: string; channelName?: string; reason?: EventLeaveReason }>();
  const toast = useToast();
  const { auth, channel, isSubscribed, hasOwnChannel, isMember } = useStore(state => ({
    auth: authSelector(state),
    channel: channelByNameSelector(channelName)(state),
    isSubscribed: channelName && isSubscribedSelector(channelName)(state),
    hasAvailableMinutes: hasAvailableMinutesSelector(state),
    hasOwnChannel: hasChannelSelector(state),
    isMember: isMemberSelector(channelName)(state)
  }));

  const [activeTab, setActiveTab] = useState<string>('ongoing');
  const [logoFailed, setLogoFailed] = useState(false);
  const [event, setEvent] = useState<{
    isLoading: boolean;
    reason?: EventLeaveReason;
    data?: EventSchema;
  }>({
    isLoading: false,
    data: undefined,
    reason: undefined
  });

  const bottomSheet = useBottomSheet<BottomPanelKey.EDIT_CHANNEL_INFO | BottomPanelKey.SHARE, { channelId?: string }>();
  const isOwnChannel = channel?.owner === auth.username;
  const isUzbekITTV = channelName === UZBEKITTV_CHANNEL_NAME;
  const hasMembershipPlan = !!channel?.membershipPricingPlan;

  useEffect(() => {
    if (router.params.eventCode) {
      fetchEvent();
    } else {
      setEvent({ isLoading: false, reason: undefined, data: undefined });
    }
  }, [router.params.channelName, router.params.eventCode, router.params.reason]);

  const fetchEvent = async () => {
    try {
      const isUserExited = router.params.reason === EventLeaveReason.EXIT;

      setEvent(prev => ({ ...prev, isLoading: true }));

      const { data: eventData } = await api.requests.ltv.getEvent(router.params.eventCode!);

      if (isUserExited) {
        setEvent(prev => ({ ...prev, isLoading: false, reason: EventLeaveReason.EXIT, data: eventData }));
        return;
      }

      if (eventData.status === EventStatus.ENDED) {
        setEvent(prev => ({ ...prev, isLoading: false, reason: EventLeaveReason.ENDED, data: eventData }));
        return;
      }

      setEvent(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      LoggerService.log('FETCH_EVENT', error);
      router.push(router.pathname);
    }
  };

  const subscribe = async (channelId: string) => {
    try {
      tracker.subscribeToChannel(channelId, channelName as string, AppTracker.ChannelSubscriptionSource.CHANNEL_PAGE);
      dispatch(profileActions.addChannel(channel as ChannelSchema));

      await api.requests.ltv.subscribeToChannel(channelId);
      tracker.subscribeToChannelSuccess(
        channelId,
        channelName as string,
        AppTracker.ChannelSubscriptionSource.CHANNEL_PAGE
      );

      api.requests.ltv
        .getSubscribedChannels()
        .then(response => {
          dispatch(profileActions.setChannels(response.data));
        })
        .catch(e => {
          LoggerService.log('GET_SUBSCRIBED_CHANNELS', e);
        });
    } catch (error) {
      LoggerService.log('SUBSCRIBE_TO_CHANNEL', error);

      if (error instanceof Error) {
        tracker.subscribeToChannelFail(
          channelId,
          channelName as string,
          AppTracker.ChannelSubscriptionSource.CHANNEL_PAGE,
          error
        );
      }
      dispatch(profileActions.removeChannel({ channelId }));

      toast.error('Subscribe request is failed', { id: `SUBSCRIBE_TO_CHANNEL:${channelId}` });
    }
  };

  const unsubscribe = async (channelId: string) => {
    try {
      tracker.unsubscribeFromChannel(
        channelId,
        channelName as string,
        AppTracker.ChannelUnsubscriptionSource.CHANNEL_PAGE
      );
      dispatch(profileActions.removeChannel({ channelId }));

      await api.requests.ltv.unsubscribeFromChannel(channelId);
      tracker.unsubscribeFromChannelSuccess(
        channelId,
        channelName as string,
        AppTracker.ChannelUnsubscriptionSource.CHANNEL_PAGE
      );

      api.requests.ltv
        .getSubscribedChannels()
        .then(response => {
          dispatch(profileActions.setChannels(response.data));
        })
        .catch(e => {
          LoggerService.log('GET_SUBSCRIBED_CHANNELS', e);
        });
    } catch (error) {
      LoggerService.log('UNSUBSCRIBE_FROM_CHANNEL', error);

      if (error instanceof Error) {
        tracker.unsubscribeFromChannelFail(
          channelId,
          channelName as string,
          AppTracker.ChannelUnsubscriptionSource.CHANNEL_PAGE,
          error
        );
      }
      dispatch(profileActions.addChannel(channel as ChannelSchema));

      toast.error('Unsubscribe request is failed', { id: `SUBSCRIBE_TO_CHANNEL:${channelId}` });
    }
  };

  const handleOnOpenSharePanel = () => {
    bottomSheet.open(BottomPanelKey.SHARE);
  };

  const handleOnEdit = () => {
    if (channel?.owner === auth.username) {
      bottomSheet.open(BottomPanelKey.EDIT_CHANNEL_INFO, { params: { channelId: channel.id } });
    }
  };

  const handleOnWatch = (_channelName: string, _eventCode: string) => {
    tracker.watchEvent(_eventCode);
    router.push(getEventURL(_channelName, _eventCode));
  };

  const handleOnBecomeMember = () => {
    if (!channel) return;

    onBecomeMember?.(channel.id, channel.name);
  };

  const handleOnSubscribe = () => {
    if (!auth.isAuth) {
      dispatch(
        callbackTriggersActions.set([
          {
            type: UserActionType.REDIRECT,
            payload: { path: getChannelURL(channelName as string) }
          }
        ])
      );

      router.push('/login');
    } else {
      subscribe(channel?.id as string);
    }
  };

  const handleOnUnsubscribe = () => {
    if (!auth.isAuth) {
      dispatch(
        callbackTriggersActions.set([
          {
            type: UserActionType.REDIRECT,
            payload: { path: getChannelURL(channelName as string) }
          }
        ])
      );

      router.push('/login');
    } else {
      unsubscribe(channel?.id as string);
    }
  };

  const handleOnEditChannelSubmitted = () => {
    bottomSheet.close();
  };

  const handleOnDismissPostEvent = () => {
    router.replace(getChannelURL(channelName));
  };

  const renderEdit = () => (
    <button type="button" className={styles.edit} onClick={handleOnEdit}>
      <EditIcon className="mr-6" />
      <span>Edit details</span>
    </button>
  );

  const renderSubscribe = () => {
    // TODO: Find another solution for uzbekittv channel custom buttons
    if (isUzbekITTV) {
      return null;
    }

    if (isSubscribed) {
      return (
        <button type="button" className={styles.subscribed} onClick={handleOnUnsubscribe}>
          <CheckedIcon className="mr-6" />
          <span>Subscribed</span>
        </button>
      );
    }

    return (
      <button type="button" className={cx(styles.subscribe, { [styles.single]: isMember })} onClick={handleOnSubscribe}>
        Subscribe
      </button>
    );
  };

  const renderBecomeMember = () => {
    if (isMember) return null;

    if (hasMembershipPlan) {
      return (
        <button type="button" className={styles.becomeMember} onClick={handleOnBecomeMember}>
          {/* // TODO: Find another solution for uzbekittv channel custom buttons */}
          {isUzbekITTV ? "Obuna bo'lish" : 'Become a member'}
        </button>
      );
    }

    return null;
  };

  const renderPostEventDetails = () => {
    if (event.isLoading) {
      return (
        <div className="container py-16">
          <Card className="text-center py-24">
            <Spinner />
          </Card>
        </div>
      );
    }

    if (event.data && event.reason && channel) {
      return (
        <div className="container py-16">
          <PostEventCard reason={event.reason} eventName={event.data.name} onDismiss={handleOnDismissPostEvent} />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Navbar
        title="Channel details"
        className={styles.header}
        right={
          <button type="button" className={styles.share} onClick={handleOnOpenSharePanel}>
            <ShareIcon width={24} height={24} />
          </button>
        }
      />

      {renderPostEventDetails()}

      <div className={styles.container}>
        <div className={styles.details}>
          <div className={styles.main}>
            <div className={styles.logo}>
              {!channel?.logo || logoFailed ? (
                <NoPhotoIcon className={styles.placeholder} />
              ) : (
                <img src={channel.logo} alt="channel logo" onError={() => setLogoFailed(true)} />
              )}
            </div>
            <div className={styles.content}>
              <p className={styles.name}>@{channelName}</p>
              <ul className={styles.counts}>
                <li>{displayCount(channel?.subscribersCount || 0)} subscribers</li>
                <li>{displayCount(channel?.finishedEventsCount || 0)} events</li>
              </ul>
            </div>
          </div>
          {isMember && (
            <div className={styles.isMember}>
              <CheckedIcon />
              <span>{isUzbekITTV ? 'Siz obunachisiz' : 'You’re a member'}</span>
            </div>
          )}
          {channel?.description && <div className={styles.description}>{channel?.description}</div>}
        </div>

        <div className={styles.actions}>
          {isOwnChannel ? (
            renderEdit()
          ) : (
            <>
              {renderSubscribe()}
              {renderBecomeMember()}
            </>
          )}
        </div>

        <div className={styles.events}>
          <ul className={styles.tabs}>
            <li>
              <button
                type="button"
                className={cx(styles.tab, { [styles.active]: activeTab === 'ongoing' })}
                onClick={() => setActiveTab('ongoing')}
              >
                Ongoing
              </button>
            </li>
            <li>
              <button
                type="button"
                className={cx(styles.tab, { [styles.active]: activeTab === 'upcoming' })}
                onClick={() => setActiveTab('upcoming')}
              >
                Upcoming
              </button>
            </li>
            <li>
              <button
                type="button"
                className={cx(styles.tab, { [styles.active]: activeTab === 'past' })}
                onClick={() => setActiveTab('past')}
              >
                Past
              </button>
            </li>
          </ul>

          <div className={styles.list}>
            <ChannelEvents activeTab={activeTab} channelName={channelName} onWatch={handleOnWatch} />
          </div>
        </div>
      </div>

      <BottomPanels
        panels={[
          {
            enabled: hasOwnChannel,
            name: BottomPanelKey.EDIT_CHANNEL_INFO,
            show: bottomSheet.state.contentKey === BottomPanelKey.EDIT_CHANNEL_INFO,
            title: 'Edit channel info',
            component: (
              <EditChannelPanel
                channelId={bottomSheet.state.params?.channelId as string}
                onSubmitted={handleOnEditChannelSubmitted}
              />
            )
          },
          {
            name: BottomPanelKey.SHARE,
            show: bottomSheet.state.contentKey === BottomPanelKey.SHARE,
            title: isDesktop ? 'Share it via social media' : 'Share this channel',
            component: <SharePanel channel={channel as ChannelSchema} />
          }
        ]}
        onClose={bottomSheet.close}
      />
    </>
  );
};

export default ChannelDetails;
